import MagnifierIcon from 'app/ui/MagnifierIcon';
import { NIGHT_TINT } from 'app/constants/AppConstants.js';

export default class Furniture extends Phaser.Group {
  constructor({ game, location, data }) {
    super(game);
    this.location = location;
    this.data = data;
    this.id = data.id;
    this.dz = data.dz || 0;

    //
    this.x = data.x;
    this.y = data.y;

    //
    this.onChange = new Phaser.Signal();

    // for tint
    this.tr = 0xff;
    this.tg = 0xff;
    this.tb = 0xff;
    this.tintColor = 0xffffff;

    this.emptyBB = { x: 0, y: 0, width: 0, height: 0 };

    this.createAsset();

    //
    if (this.data.button) {
      this.addButton();
    }

    //
    this.setSitPositions();
  }

  //
  createAsset() {
    this.asset = this.game.make.spine(0, 0, this.data.key, true);
    this.asset.anchor.set(0.5, 1);
    this.addChildAt(this.asset, 0);

    // get root bone
    this.rootBone = this.asset.skeleton.getRootBone();
    this.scaleDefault = this.rootBone.scaleX;
  }

  updateBounds() {
    this.bb = this.visible ? this.getBounds() : this.emptyBB;
  }

  get depth() {
    return this.y + this.dz;
  }

  get width() {
    return this.data.width || this.width;
  }

  // functions for moving furnitures like rock or log
  addObject(obj) {
    obj;
  }

  removeObject(obj) {
    obj;
  }

  //
  getTop(bb) {
    if (this.data.top) {
      const bb1 = new Phaser.Rectangle();
      const len = this.data.top.length;
      for (let i = 0; i < len; i++) {
        const top = this.data.top[i];
        if (top.empty) {
          bb1.x = this.bb.x + top.x;
          bb1.y = this.bb.y + top.y;
          bb1.width = top.w;
          bb1.height = top.h;
          if (Phaser.Rectangle.intersects(bb, bb1)) {
            return { y: bb1.y, h: top.h, character: top.character };
          }
        }
      }
    }
    return null;
  }

  getSide(bb) {
    if (this.data.side) {
      const bb1 = new Phaser.Rectangle();
      const len = this.data.side.length;
      for (let i = 0; i < len; i++) {
        const side = this.data.side[i];
        bb1.x = this.bb.x + side.x;
        bb1.y = this.bb.y + side.y;
        bb1.width = side.w;
        bb1.height = side.h;
        if (Phaser.Rectangle.intersects(bb, bb1)) {
          return { y: bb1.y, h: side.h };
        }
      }
    }
    return null;
  }

  //
  setAnim(name, loop = true, channel = 0) {
    this.currentAnim = this.asset.setAnimationByName(channel, name, loop);
  }

  pauseAnim() {
    this.currentAnim.timeScale = 0;
  }

  unpauseAnim() {
    this.currentAnim.timeScale = 1;
  }

  //
  setLight(power) {
    const r0 = 1;
    const g0 = 1;
    const b0 = 1;

    const tint = NIGHT_TINT;
    const r1 = (tint >> 16) / 255;
    const g1 = ((tint >> 8) & 0x00ff) / 255;
    const b1 = (tint & 0x0000ff) / 255;

    this.asset.skeleton.color.r = r1 + power * (r0 - r1);
    this.asset.skeleton.color.g = g1 + power * (g0 - g1);
    this.asset.skeleton.color.b = b1 + power * (b0 - b1);
  }

  setLight2(power) {
    const r0 = 0xff;
    const g0 = 0xff;
    const b0 = 0xff;

    const tint = NIGHT_TINT;
    const r1 = tint >> 16;
    const g1 = (tint >> 8) & 0x00ff;
    const b1 = tint & 0x0000ff;

    this.tr = r1 + ((power * (r0 - r1)) | 0);
    this.tg = g1 + ((power * (g0 - g1)) | 0);
    this.tb = b1 + ((power * (b0 - b1)) | 0);

    this.updateTint();
  }

  setNight() {}

  setDay() {}

  updateTint(tween, value, tweenData) {
    tween;
    tweenData;
    value;
    const r = this.tr | 0;
    const g = this.tg | 0;
    const b = this.tb | 0;
    const tint = (r << 16) | (g << 8) | b;

    this.tintColor = tint;
  }

  //
  addButton() {
    const data = this.data.button;
    this.button = this.game.make.button(data.x, data.y, 'empty');
    this.button.width = data.w;
    this.button.height = data.h;
    this.button.alpha = 0.01;
    this.addChild(this.button);
    this.button.inputEnabled = true;
    this.button.events.onInputDown.add(this.handleInputDown, this);
    this.button.events.onInputUp.add(this.handleInputUp, this);
    this.button.input.useHandCursor = true;
  }

  handleInputDown() {}

  handleInputUp() {}

  //
  addMagnifierIcon() {
    this.magnifierIcon = new MagnifierIcon(
      this.game,
      this.data.macro_view.x + this.x,
      this.data.macro_view.y + this.y,
      this
    );
    this.location.addObjectToLocation(this.magnifierIcon);

    this.macroAnimInd = 0;
  }

  showMacroView() {
    this.location.showMacroView({
      x: this.magnifierIcon.worldPosition.x,
      y: this.magnifierIcon.worldPosition.y,
      macroView: this.data.macro_view,
      furniture: this,
      animInd: this.macroAnimInd,
    });

    this.macroAnimInd++;
    this.macroAnimInd %= this.data.macro_view.animNames.length;
  }

  // sitting
  setSitPositions() {
    this.sitPoints = [];
    if (this.data.top) {
      const len = this.data.top.length;
      for (let i = 0; i < len; i++) {
        const top = this.data.top[i];
        top.empty = true;
        if (top.character === 'sit') {
          // character can sit here
          const x = this.x - this.asset.width * 0.5 + top.x + top.w * 0.5;
          const y = this.y - this.height + top.y + top.h - 25;
          const dz = this.dz - (y - this.y) + 2;
          const sp = { x: x, y: y, dz: dz, empty: true, top: top };
          this.sitPoints.push(sp);
        }
      }
    }
    return null;
  }

  canSit(x) {
    if (this.sitPoints.length > 0) {
      const len = this.sitPoints.length;
      for (let i = 0; i < len; i++) {
        const sp = this.sitPoints[i];
        if (sp.empty) {
          const dx = x - sp.x;
          if (dx < 40) {
            return sp;
          }
        }
      }
    }
    return null;
    // return this.data.sit_point && this.isEmpty;
  }

  //
  step(dt) {
    dt;
    // visibility
    const left = -this.location.x;
    const right = left + this.game.width;
    if (this.x - this.width * 0.5 > right || this.x + this.width * 0.5 < left) {
      this.visible = false;
    } else {
      this.visible = true;
    }

    // light
    let power = 0;
    if (this.visible) {
      // if visible get all lights
      power = this.location.lights.getLightAt(
        this.worldPosition.x,
        this.worldPosition.y - (this.height >> 1),
        !this.data.flashlight
      );
    } else {
      // if not visible only ambient
      power = this.location.lights.getLightAt(
        this.worldPosition.x,
        this.worldPosition.y - (this.height >> 1),
        true
      );
    }
    if (power >= 0 && power !== this.lightPower) {
      this.lightPower = power;
      this.setLight(power);
    }
  }

  //
  animateGrowUp() {
    this.scale.set(0.5);
    this.game.add.tween(this.scale).to({ x: 1, y: 1 }, 150, null, true);
  }

  pulse(time = 2000) {
    time;
    // this.game.add
    //   .tween(this.scale)
    //   .to({ x: [1.2, 1, 1.2, 1, 1.2, 1], y: [1.2, 1, 1.2, 1, 1.2, 1] }, time, null, true);
  }

  //
  serialize() {
    return { id: this.id, x: this.x, y: this.y };
  }
}
